import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { LOCATIONS } from "../data/locations";
import { Button } from "../components/ui/button";
import BookCallButton from "../components/BookCallButton";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import Seo, { breadcrumbSchema, SITE_NAME } from "../components/Seo";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

export default function AreasWeSupport() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Areas ${SITE_NAME} supports`,
    itemListElement: LOCATIONS.map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `Accountants in ${l.town}`,
      url: `${origin()}/${l.slug}`,
    })),
  };

  return (
    <div className="bg-white" data-testid="areas-we-support-page">
      <Seo
        title="Areas We Support — Accountants Across the Midlands"
        description="Cloud Accounting Support Services is based in Tamworth and supports businesses across the Midlands — Sutton Coldfield, Lichfield, Birmingham, Solihull, Cannock, Nuneaton, Hinckley, Burton upon Trent and beyond — with Xero accounting and outsourced finance."
        jsonLd={[itemList, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Areas We Support", path: "/areas-we-support" }])]}
      />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-4">
            <MapPin size={16} /> Areas we support
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Accountants across Tamworth &amp; the Midlands</h1>
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
            We're proudly based in Tamworth and act as the outsourced finance team for businesses right across the Midlands. Cloud accounting means we support clients wherever they are — with a dedicated contact, fixed monthly fees and proactive, year-round advice.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/contact" data-testid="areas-cta-talk"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <BookCallButton testId="areas-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white rounded-full px-8 py-[1.15rem] text-base font-bold transition-all bg-transparent" />
          </div>
        </div>
      </section>

      {/* Towns grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-extrabold text-navy">Choose your area</h2>
        <p className="text-slate-500 mt-2 max-w-2xl">Explore how we help businesses in each of the towns and cities we support. Don't see yours? We work with clients across the UK through cloud accounting — <Link to="/contact" className="text-orange font-semibold hover:underline">get in touch</Link>.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {LOCATIONS.map((l) => (
            <Link key={l.slug} to={`/${l.slug}`} data-testid={`areas-town-${l.slug}`}
              className="group bg-slate-50 rounded-2xl border border-slate-100 p-6 hover:border-orange hover:-translate-y-1 transition-all">
              <div className="flex items-center gap-2 text-orange mb-2"><MapPin size={16} /><span className="text-xs font-bold uppercase tracking-wide text-slate-400">{l.county}</span></div>
              <h3 className="font-display font-bold text-navy text-lg">Accountants in {l.town}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mt-2 line-clamp-3">{l.intro[0]}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-orange mt-4 group-hover:gap-3 transition-all">View {l.town} <ArrowRight size={15} /></span>
            </Link>
          ))}
        </div>
      </section>

      <GoogleReviewsSection testId="areas-reviews" eyebrow="Trusted across the Midlands" />

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Wherever you are, let's talk</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Book a free, no-obligation chat. We'll learn about your business and show you exactly how we can help.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact"><Button className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 py-6 text-base font-bold">Let's Talk <ArrowRight size={18} className="ml-2" /></Button></Link>
            <a href="tel:+441827780550"><Button variant="outline" className="rounded-full px-8 py-6 text-base font-bold border-white/25 text-white hover:bg-white/10 bg-transparent"><Phone size={16} className="mr-2" /> 01827 780550</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
